import { searchAssets, useUserContext } from "@dexteel/mesf-core";
import { useMemo } from "react";

export type UserScope = {
	isLoading: boolean;
	// Root default (Plant): no limits
	isFull: boolean;
	// Null when full
	defaultAsset: { AssetId: number; AssetName: string } | null;
	// Default asset has lines under it
	isArea: boolean;
	// Default asset or anything under it
	includes: (assetId: number) => boolean;
	// Under or above the default asset
	covers: (assetName: string) => boolean;
};

const FULL_SCOPE: UserScope = {
	isLoading: false,
	isFull: true,
	defaultAsset: null,
	isArea: false,
	includes: () => true,
	covers: () => true,
};

const LOADING_SCOPE: UserScope = { ...FULL_SCOPE, isLoading: true };

export const useUserScope = (): UserScope => {
	const {
		state: { defaultAreaId, permissions },
		isLoading: isUserLoading,
	} = useUserContext();
	const { data: assets, isError } = searchAssets();

	return useMemo(() => {
		if (isUserLoading) return LOADING_SCOPE;
		// Default never loads without SEC.GetUser
		if ((isError && !assets) || !permissions.includes("SEC.GetUser"))
			return FULL_SCOPE;
		if (defaultAreaId == null || !assets) return LOADING_SCOPE;

		const parentOf = new Map(assets.map((a) => [a.AssetId, a.ParentAssetId]));
		const isUnder = (assetId: number, rootId: number) => {
			let current: number | null | undefined = assetId;
			for (let i = 0; current != null && i <= assets.length; i++) {
				if (current === rootId) return true;
				current = parentOf.get(current);
			}
			return false;
		};

		const defaultId = Number(defaultAreaId);
		const defaultAsset = assets.find((a) => a.AssetId === defaultId);
		if (!defaultAsset || defaultAsset.ParentAssetId == null) return FULL_SCOPE;

		return {
			isLoading: false,
			isFull: false,
			defaultAsset,
			isArea: defaultAsset.IsBranch,
			includes: (assetId) => isUnder(assetId, defaultId),
			covers: (assetName) => {
				const asset = assets.find((a) => a.AssetName === assetName);
				return (
					!!asset &&
					(isUnder(asset.AssetId, defaultId) ||
						isUnder(defaultId, asset.AssetId))
				);
			},
		};
	}, [isUserLoading, isError, permissions, defaultAreaId, assets]);
};
